import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Heatmap } from "../components/Heatmap";
import { DepartmentPicker, DeptChips, useDepartments } from "../components/DepartmentPicker";
import {
  UsersThree, ClipboardText, WarningCircle, BookOpen,
  ChatCircleText, CheckCircle, XCircle, Clock, Swap, Megaphone, Trash, PaperPlaneTilt,
  GearSix, Plus, X,
} from "@phosphor-icons/react";

const STATUS_STYLE = {
  green: { dot: "bg-emerald-500", chip: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Ready" },
  amber: { dot: "bg-amber-500", chip: "bg-amber-50 text-amber-700 border-amber-200", label: "Pending" },
  red: { dot: "bg-red-500", chip: "bg-red-50 text-red-700 border-red-200", label: "Non-compliant" },
};

export function timeGreeting(d = new Date()) {
  const h = d.getHours();
  if (h >= 5 && h < 12) return "Good Morning";
  if (h >= 12 && h < 17) return "Good Afternoon";
  if (h >= 17 && h < 22) return "Good Evening";
  return "Good Late Shift";
}

function Stat({ icon: Icon, label, value, tone = "slate" }) {
  const tones = {
    slate: "text-slate-900 bg-slate-100", green: "text-emerald-600 bg-emerald-50",
    red: "text-red-600 bg-red-50", amber: "text-amber-600 bg-amber-50",
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 card-lift" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${tones[tone]}`}>
        <Icon size={22} weight="duotone" />
      </div>
      <p className="mt-4 font-heading text-3xl font-black">{value}</p>
      <p className="text-sm text-slate-500 font-medium mt-1">{label}</p>
    </div>
  );
}

function CategorySettings({ categories, onSaved, onClose }) {
  const [cats, setCats] = useState(categories);
  const [draft, setDraft] = useState("");
  const addCat = () => {
    const v = draft.trim();
    if (!v) return;
    if (cats.some((c) => c.toLowerCase() === v.toLowerCase())) return toast.error("Category already exists");
    setCats([...cats, v]);
    setDraft("");
  };
  const save = async () => {
    try {
      const { data } = await api.put("/alert-config", { categories: cats });
      onSaved(data.categories);
      toast.success("Alert categories updated");
      onClose();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="mt-4 p-5 rounded-xl border border-slate-200 bg-slate-50/70" data-testid="alert-category-settings">
      <p className="text-sm font-bold mb-3">Manage alert categories</p>
      <div className="flex flex-wrap gap-2 mb-4">
        {cats.map((c) => (
          <span key={c} data-testid={`alert-category-chip-${c.toLowerCase().replace(/\s+/g, "-")}`}
            className="inline-flex items-center gap-1.5 text-xs font-bold bg-white border border-slate-200 px-3 py-1.5 rounded-full">
            {c}
            <button data-testid={`alert-category-remove-${c.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setCats(cats.filter((x) => x !== c))} className="text-slate-300 hover:text-red-500 tap-press">
              <X size={14} />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input data-testid="alert-category-new-input" value={draft} onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addCat()} placeholder="New category name"
          className="flex-1 h-11 px-4 rounded-xl border border-slate-200 bg-white text-sm" />
        <button data-testid="alert-category-add-button" onClick={addCat}
          className="h-11 px-4 rounded-xl border border-slate-200 bg-white text-sm font-bold flex items-center gap-1.5 tap-press hover:bg-slate-100">
          <Plus size={16} /> Add
        </button>
        <button data-testid="alert-category-save-button" onClick={save}
          className="h-11 px-5 rounded-full bg-slate-900 text-white text-sm font-bold tap-press hover:bg-slate-800">Save</button>
      </div>
    </div>
  );
}

function AlertsManager({ canEditCategories }) {
  const [alerts, setAlerts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({ category: "", label: "", department_ids: [] });
  const [viewDept, setViewDept] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const departments = useDepartments();

  const load = (dep) => api.get("/venue-alerts", { params: dep ? { department_id: dep } : {} })
    .then((r) => setAlerts(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/alert-config").then((r) => {
      setCategories(r.data.categories || []);
      setForm((f) => ({ ...f, category: f.category || (r.data.categories || [])[0] || "" }));
    }).catch(() => {});
  }, []);
  useEffect(() => { load(viewDept); }, [viewDept]);

  const add = async () => {
    if (!form.label.trim()) return toast.error("Alert text is required");
    try {
      await api.post("/venue-alerts", form);
      setForm({ ...form, label: "", department_ids: [] });
      toast.success("Alert broadcast — staff will see it in today's briefing");
      load(viewDept);
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/venue-alerts/${id}`); load(viewDept); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8" data-testid="alerts-manager">
      <div className="flex flex-wrap items-center gap-3 mb-2">
        <Megaphone size={24} weight="duotone" className="text-amber-500" />
        <h2 className="font-heading text-xl font-bold flex-1">Today's Alerts</h2>
        <select data-testid="alerts-view-dept-filter" value={viewDept} onChange={(e) => setViewDept(e.target.value)}
          className="h-10 px-3 rounded-xl border border-slate-200 bg-white text-sm font-semibold">
          <option value="">All departments</option>
          {departments.filter((d) => d.active !== false).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        {canEditCategories && (
          <button data-testid="alert-category-settings-button" onClick={() => setShowSettings(!showSettings)}
            className="h-10 px-3.5 rounded-xl border border-slate-200 text-sm font-bold flex items-center gap-1.5 tap-press hover:bg-slate-50">
            <GearSix size={17} /> Categories
          </button>
        )}
      </div>
      <p className="text-sm text-slate-500 mb-5">Broadcast announcements, daily focus items, VIP arrivals or urgent alerts — staff see these at check-in.</p>
      {showSettings && <CategorySettings categories={categories}
        onSaved={(c) => { setCategories(c); if (!c.includes(form.category)) setForm({ ...form, category: c[0] || "" }); }}
        onClose={() => setShowSettings(false)} />}
      <div className="flex flex-col sm:flex-row gap-3 mb-5 mt-4">
        <select data-testid="alert-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
          className="h-12 px-4 rounded-xl border border-slate-200 bg-white font-semibold sm:w-48">
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <input data-testid="alert-label-input" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="e.g. VIP suite arrival at 3 PM — prepare welcome amenities" className="flex-1 h-12 px-4 rounded-xl border border-slate-200" />
        <button data-testid="alert-publish-button" onClick={add}
          className="h-12 px-6 rounded-full bg-slate-900 text-white text-sm font-bold flex items-center justify-center gap-2 tap-press hover:bg-slate-800">
          <PaperPlaneTilt size={18} /> Broadcast
        </button>
      </div>
      <div className="mb-5"><DepartmentPicker label="Broadcast to departments (blank = everyone)" value={form.department_ids} testId="alert-dept-picker"
        onChange={(ids) => setForm({ ...form, department_ids: ids })} /></div>
      <div className="space-y-2" data-testid="alerts-manager-list">
        {alerts.map((a) => (
          <div key={a.id} data-testid={`alert-row-${a.id}`}
            className="flex items-center gap-3 p-3.5 rounded-xl border border-slate-100 bg-slate-50/60">
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 bg-white border border-slate-200 px-2.5 py-1 rounded-full shrink-0">{a.category || a.type}</span>
            <p className="flex-1 text-sm font-semibold leading-snug">{a.label}</p>
            <DeptChips ids={a.department_ids} departments={departments} testId={`alert-dept-chips-${a.id}`} />
            <button data-testid={`alert-delete-${a.id}`} onClick={() => remove(a.id)}
              className="p-2 text-slate-300 hover:text-red-500 tap-press shrink-0"><Trash size={18} /></button>
          </div>
        ))}
        {alerts.length === 0 && <p className="text-sm text-slate-400">No live alerts{viewDept ? " for this department" : ""} — broadcast the first one above.</p>}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [readiness, setReadiness] = useState([]);
  const [handover, setHandover] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [venues, setVenues] = useState([]);
  const [deptFilter, setDeptFilter] = useState("");
  const [venueFilter, setVenueFilter] = useState("");
  const [comparison, setComparison] = useState(null);
  const on = (k) => user.features?.[k] !== false;
  const scopedMgr = user.role === "manager" && (user.department_ids || []).length > 0 && !user.is_master_operator;
  const adminLike = ["admin", "gm", "developer"].includes(user.role) || user.is_master_operator === true;

  useEffect(() => {
    api.get("/departments").then((r) => setDepartments(r.data)).catch(() => {});
    if (!scopedMgr) api.get("/venues").then((r) => setVenues(r.data)).catch(() => {});
    if (on("audits")) api.get("/handover/pending").then((r) => setHandover(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const params = {};
    if (deptFilter) params.department_id = deptFilter;
    else if (venueFilter) params.venue_id = venueFilter;
    api.get("/dashboard/stats", { params }).then((r) => setStats(r.data));
    if (on("briefings")) api.get("/readiness", { params }).then((r) => setReadiness(r.data)).catch(() => {});
    if (!scopedMgr) api.get("/dashboard/dept-comparison", { params: venueFilter ? { venue_id: venueFilter } : {} })
      .then((r) => setComparison(r.data)).catch(() => {});
  }, [deptFilter, venueFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const deptOptions = departments.filter((d) => d.active !== false && (!venueFilter || d.venue_id === venueFilter || !d.venue_id));

  const ackHandover = async () => {
    await api.post(`/handover/${handover.id}/ack`);
    setHandover(null);
  };

  return (
    <div className="space-y-8 rise-in" data-testid="manager-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-slate-400">{user.org_name || "Property Operations"}</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-2" data-testid="dashboard-greeting">
            {timeGreeting()}, {user.name.split(" ")[0]}.
          </h1>
        </div>
        {!scopedMgr && (departments.length > 0 || venues.length > 0) && (
          <div className="flex flex-col sm:flex-row gap-3 shrink-0" data-testid="dashboard-scope-switcher">
            {venues.length > 0 && (
              <select data-testid="dashboard-venue-filter" value={venueFilter}
                onChange={(e) => { setVenueFilter(e.target.value); setDeptFilter(""); }}
                className="h-12 px-4 rounded-xl border border-slate-200 bg-white font-semibold text-sm">
                <option value="">All venues — property-wide</option>
                {venues.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            )}
            {departments.length > 0 && (
              <select data-testid="dashboard-dept-filter" value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)}
                className="h-12 px-4 rounded-xl border border-slate-200 bg-white font-semibold text-sm">
                <option value="">All departments</option>
                {deptOptions.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            )}
          </div>
        )}
        {scopedMgr && (
          <span data-testid="dashboard-scoped-badge" className="text-xs font-bold bg-sky-50 text-sky-700 border border-sky-200 px-4 py-2 rounded-full shrink-0">
            Department dashboard — scoped to your department{(user.department_ids || []).length > 1 ? "s" : ""}
          </span>
        )}
      </div>

      {comparison && comparison.departments.length > 0 && !scopedMgr && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8" data-testid="dept-comparison">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <h2 className="font-heading text-xl font-bold">Department readiness — {venueFilter ? "venue overview" : "property overview"}</h2>
            <div className="flex gap-4 text-xs font-bold">
              <span data-testid="pending-mgr-handovers" className={comparison.pending_manager_handovers > 0 ? "text-amber-600" : "text-slate-400"}>
                {comparison.pending_manager_handovers} exec handover{comparison.pending_manager_handovers !== 1 ? "s" : ""} pending
              </span>
              <span data-testid="urgent-alert-count" className={comparison.urgent_alerts > 0 ? "text-red-600" : "text-slate-400"}>
                {comparison.urgent_alerts} urgent alert{comparison.urgent_alerts !== 1 ? "s" : ""} (36h)
              </span>
            </div>
          </div>
          <div className="space-y-3">
            {comparison.departments.map((d) => (
              <div key={d.id} className="flex items-center gap-4" data-testid={`dept-compare-${d.id}`}>
                <p className="w-36 sm:w-44 text-sm font-bold truncate shrink-0">{d.name}</p>
                <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${d.readiness_pct >= 80 ? "bg-emerald-500" : d.readiness_pct >= 40 ? "bg-amber-400" : "bg-red-400"}`}
                    style={{ width: `${Math.max(d.readiness_pct, d.staff_total ? 4 : 0)}%` }} />
                </div>
                <p className="w-28 text-xs font-bold text-slate-500 text-right shrink-0">{d.staff_ready}/{d.staff_total} ready · {d.tasks_completed}/{d.tasks_total} tasks</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {on("audits") && handover && (
        <div data-testid="handover-pending-banner"
          className="bg-amber-50 border border-amber-200 border-l-4 border-l-amber-500 rounded-2xl p-6 flex flex-col sm:flex-row sm:items-center gap-4 rise-in">
          <Swap size={30} weight="duotone" className="text-amber-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-heading font-bold text-amber-900">Handover pending review — {handover.shift_label} shift by {handover.created_by_name}</p>
            <p className="text-sm text-amber-800 mt-1 line-clamp-2">{handover.summary}</p>
          </div>
          <div className="flex gap-3 shrink-0">
            <Link to="/handover" data-testid="handover-banner-view"
              className="h-11 px-5 rounded-full border border-amber-300 text-amber-900 text-sm font-bold flex items-center tap-press hover:bg-amber-100">View details</Link>
            <button data-testid="handover-banner-ack" onClick={ackHandover}
              className="h-11 px-5 rounded-full bg-slate-900 text-white text-sm font-bold tap-press hover:bg-slate-800">Acknowledge</button>
          </div>
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {on("briefings") && <Stat icon={UsersThree} label="Staff ready today" value={`${stats.staff_green}/${stats.staff_total}`} tone={stats.staff_green === stats.staff_total ? "green" : "amber"} />}
          {on("tasks") && <Stat icon={ClipboardText} label="Tasks completed" value={`${stats.tasks_completed}/${stats.tasks_total}`} tone="slate" />}
          {on("tasks") && <Stat icon={WarningCircle} label="Tasks overdue" value={stats.tasks_overdue} tone={stats.tasks_overdue > 0 ? "red" : "green"} />}
          {on("reviews") && <Stat icon={ChatCircleText} label="Negative reviews (7d)" value={stats.negative_reviews_week} tone={stats.negative_reviews_week > 0 ? "amber" : "green"} />}
        </div>
      )}

      <div className="grid lg:grid-cols-12 gap-6">
        {on("briefings") && (
        <div className="lg:col-span-7 bg-white border border-slate-200 rounded-2xl p-6 sm:p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-xl font-bold">Shift Readiness</h2>
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-slate-400">Today</span>
          </div>
          <div className="space-y-3" data-testid="readiness-list">
            {readiness.map((s) => {
              const st = STATUS_STYLE[s.status];
              return (
                <div key={s.user_id} data-testid={`readiness-row-${s.user_id}`}
                  className={`flex items-center gap-4 p-4 rounded-xl border border-slate-100 border-l-4 ${
                    s.status === "green" ? "border-l-emerald-500" : s.status === "red" ? "border-l-red-500" : "border-l-amber-400"}`}>
                  <span className={`h-3 w-3 rounded-full ${st.dot} ${s.status === "amber" ? "pulse-dot" : ""}`} />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm">{s.name}</p>
                    <p className="text-xs text-slate-400">{s.position}</p>
                  </div>
                  {s.total != null && (
                    <span className="text-xs text-slate-500 font-semibold flex items-center gap-1">
                      {s.status === "green" ? <CheckCircle size={16} className="text-emerald-500" /> : <XCircle size={16} className="text-red-500" />}
                      Quiz {s.correct}/{s.total}
                    </span>
                  )}
                  {s.total == null && <span className="text-xs text-slate-400 flex items-center gap-1"><Clock size={16} /> Not started</span>}
                  <span className={`text-xs font-bold px-3 py-1.5 rounded-full border ${st.chip}`}>{st.label}</span>
                </div>
              );
            })}
            {readiness.length === 0 && <p className="text-slate-400 text-sm">No staff on roster yet.</p>}
          </div>
        </div>
        )}

        <div className="lg:col-span-5 space-y-6">
          {on("sops") && stats && (
            <div className="bg-slate-900 text-white rounded-2xl p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-4">
                <BookOpen size={24} weight="duotone" className="text-emerald-400" />
                <h2 className="font-heading text-xl font-bold">Content Vault</h2>
              </div>
              <p className="font-heading text-3xl sm:text-4xl font-black">{stats.sop_count}</p>
              <p className="text-slate-400 text-sm mt-1">published SOPs live for your team</p>
              <div className="flex gap-3 mt-5">
                <Link to="/sops" data-testid="dashboard-sops-link" className="flex-1 h-11 rounded-full bg-white text-slate-900 font-bold text-sm flex items-center justify-center tap-press">Browse vault</Link>
                <Link to="/sop-studio" data-testid="dashboard-sop-studio-link" className="flex-1 h-11 rounded-full border border-white/30 font-bold text-sm flex items-center justify-center tap-press hover:bg-white/10">SOP Studio</Link>
              </div>
            </div>
          )}

          {on("briefings") && stats && (
            <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8" data-testid="readiness-summary-card">
              <h2 className="font-heading text-xl font-bold mb-4">Readiness pulse</h2>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="rounded-xl bg-emerald-50 border border-emerald-100 py-4">
                  <p className="font-heading text-2xl font-black text-emerald-600">{stats.staff_green}</p>
                  <p className="text-xs font-bold text-emerald-700 mt-1">Ready</p>
                </div>
                <div className="rounded-xl bg-amber-50 border border-amber-100 py-4">
                  <p className="font-heading text-2xl font-black text-amber-600">{stats.staff_pending}</p>
                  <p className="text-xs font-bold text-amber-700 mt-1">Pending</p>
                </div>
                <div className="rounded-xl bg-red-50 border border-red-100 py-4">
                  <p className="font-heading text-2xl font-black text-red-600">{stats.staff_red}</p>
                  <p className="text-xs font-bold text-red-700 mt-1">Red</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {on("briefings") && <AlertsManager canEditCategories={adminLike} />}

      {on("tasks") && stats?.staff_notes?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 border-l-4 border-l-amber-400" data-testid="staff-notes-card">
          <h2 className="font-heading text-xl font-bold mb-1">Staff task notes today</h2>
          <p className="text-sm text-slate-500 mb-4">Progress comments left during task completion — review partial completions.</p>
          <div className="space-y-2">
            {stats.staff_notes.map((n) => (
              <div key={n.id} data-testid={`staff-note-${n.id}`} className="flex items-start gap-3 bg-amber-50/60 border border-amber-100 rounded-xl px-4 py-3">
                <div className="flex-1">
                  <p className="text-sm font-bold">{n.title}</p>
                  <p className="text-sm text-slate-700 mt-0.5">"{n.note}"</p>
                </div>
                <span className="text-xs font-semibold text-slate-400 shrink-0">{n.completed_by}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {on("analytics") && <Heatmap departmentId={scopedMgr ? "" : deptFilter} venueId={scopedMgr ? "" : venueFilter} />}
    </div>
  );
}
